import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";

import ContractDetails from "../../Contracts/ContractDetails";
import ensureAllowance from "../../Common/ensureAllowance";
import Loader from "../../Component/Loader/Loader";
import { approveRewardRequest, fetchAllRewards } from "../../Actions/adminActions";
import { toast } from "react-toastify";
import { parseUnits } from "viem";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";

const RewardDappDetails = () => {
  const { BigInt } = window;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: walletClient } = useWalletClient();
  const { address: account } = useAccount();
  const publicClient = usePublicClient();
  const [loading, setLoading] = useState(false);

  const {
    amountArray = [],
    walletAddressArray = [],
    usernameArray = [],
    requestIdsArray = [],
    itemsArray = [],
    rewardItemsArray = [],
  } = location.state || {};

  async function handleRewardTransfer() {
    try {
      if (!walletClient || !account) {
        toast.error("Please connect your wallet.");
        return;
      }
      if (
        !Array.isArray(amountArray) ||
        !Array.isArray(walletAddressArray) ||
        !Array.isArray(requestIdsArray) ||
        amountArray.length === 0 ||
        amountArray.length !== walletAddressArray.length ||
        amountArray.length !== requestIdsArray.length ||
        walletAddressArray.some((a) => !a)
      ) {
        toast.error("Invalid reward data. Arrays must have the same length.");
        return;
      }

      setLoading(true);

      // Parse to token decimals (18). If your token is 6 decimals, change 18 to 6.
      const parsedAmounts = amountArray.map((amt) =>
        parseUnits(amt.toString(), 18) // bigint
      );
      const totalAmount = parsedAmounts.reduce((acc, a) => acc + a, 0n);

      const parsedTxIds = requestIdsArray.map((id) => BigInt(id));

      // Ensure allowance for contract spend
      const allowanceOk = await ensureAllowance({
        account,
        publicClient,
        walletClient,
        token: ContractDetails.BUSD,
        spender: ContractDetails.contract,
        amount: totalAmount,
      });

      if (!allowanceOk) {
        toast.error("Allowance failed.");
        return;
      }

      // 1) Simulate first (same style as 2WithdrawDetails.jsx)
      const { result } = await publicClient.simulateContract({
        abi: ContractDetails.contractABI,
        address: ContractDetails.contract,
        functionName: "sendUSDTToMultipleAddresses",
        args: [parsedAmounts, walletAddressArray, parsedTxIds],
        account,
        value: 0n,
      });

      // Contract returns: (processedCount, duplicateTxIds, duplicateBlockNumbers)
      const processedCount = result?.[0];
      const duplicateTxIds = (result?.[1] || []).map((x) => x.toString());

      // Split duplicates vs non-duplicates (we will still approve duplicates in API,
      // because they were already paid in a previous on-chain transaction)
      const duplicateSet = new Set(duplicateTxIds);
      const duplicatePayload = {
        requestIds: [],
        items: [],
        rewardItems: [],
      };

      // Non-duplicates (need on-chain tx now)
      const filtered = {
        amounts: [],
        addresses: [],
        txids: [],
        requestIds: [],
        items: [],
        rewardItems: [],
      };

      for (let i = 0; i < parsedTxIds.length; i++) {
        const txidStr = parsedTxIds[i].toString();
        if (duplicateSet.has(txidStr)) {
          duplicatePayload.requestIds.push(requestIdsArray[i]);
          duplicatePayload.items.push(itemsArray[i]);
          duplicatePayload.rewardItems.push(rewardItemsArray[i]);
          continue;
        }
        filtered.amounts.push(parsedAmounts[i]);
        filtered.addresses.push(walletAddressArray[i]);
        filtered.txids.push(parsedTxIds[i]);
        filtered.requestIds.push(requestIdsArray[i]);
        filtered.items.push(itemsArray[i]);
        filtered.rewardItems.push(rewardItemsArray[i]);
      }

      // If everything is duplicate → DO NOT SEND TX, but DO approve in API
      if (!processedCount || processedCount === 0n) {
        if (duplicatePayload.requestIds.length > 0) {
          await dispatch(
            approveRewardRequest(duplicatePayload.requestIds, "already_paid", "dap")
          );
          await dispatch(
            fetchAllRewards({
              page: 1,
              limit: 20,
              status: "1",
              paid: "0",
              requested: "1",
            })
          );
        }
        navigate("/dashboard/rewards");
        return;
      }

      if (filtered.amounts.length === 0) {
        // no on-chain tx needed; just approve duplicates
        if (duplicatePayload.requestIds.length > 0) {
          await dispatch(
            approveRewardRequest(duplicatePayload.requestIds, "already_paid", "dap")
          );
          await dispatch(
            fetchAllRewards({
              page: 1,
              limit: 20,
              status: "1",
              paid: "0",
              requested: "1",
            })
          );
        }
        navigate("/dashboard/rewards");
        return;
      }

      // Re-simulate with filtered arrays (like withdrawal flow does)
      const { request: filteredRequest } = await publicClient.simulateContract({
        abi: ContractDetails.contractABI,
        address: ContractDetails.contract,
        functionName: "sendUSDTToMultipleAddresses",
        args: [filtered.amounts, filtered.addresses, filtered.txids],
        account,
        value: 0n,
      });

      const txHash = await walletClient.writeContract(filteredRequest);
      const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });

      if (receipt.status !== "success") {
        toast.error("Transaction failed.");
        return;
      }

      // 3) After chain success: approve non-duplicates (just transferred)
      await dispatch(
        approveRewardRequest(filtered.requestIds, txHash, "dap")
      );

      // Also approve duplicates (already transferred earlier)
      if (duplicatePayload.requestIds.length > 0) {
        await dispatch(
          approveRewardRequest(duplicatePayload.requestIds, "already_paid", "dap")
        );
      }
      await dispatch(
        fetchAllRewards({
          page: 1,
          limit: 20,
          status: "1",
          paid: "0",
          requested: "1",
        })
      );

      toast.success("Reward approval completed.");
      navigate("/dashboard/rewards");
    } catch (error) {
      const message = error?.shortMessage || error?.message || "Transaction failed.";
      if (error?.code === 4001 || error?.code === "ACTION_REJECTED") {
        toast.error("Transaction rejected by user.");
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleCancel = () => {
    navigate("/dashboard/rewards");
  };

  return (
    <React.Fragment>
      {loading ? <Loader /> : null}
      <Container>
        <div id="directIncomeDiv">
          <div className="button-container">
            <button className="action-btn" onClick={handleRewardTransfer}>
              Proceed
            </button>
            <button className="action-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>

          <Row id="directTableRow" className="text-center">
            <Col md="12" className="p-0">
              <div style={{ width: "100%", display: "flex" }}>
                <table>
                  <tr>
                    <th>S. No.</th>
                    <th>Username</th>
                    <th>Wallet Address</th>
                    <th>Amount</th>
                  </tr>
                  <tbody>
                    {amountArray.map((amount, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{usernameArray[i] ?? "-"}</td>
                        <td>{walletAddressArray[i] ?? "-"}</td>
                        <td>{amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </div>
        {/* <ToastContainer /> */}
      </Container>
    </React.Fragment>
  );
};

export default RewardDappDetails;

